import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { saveAs as saveAsFile } from 'file-saver';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useLatestVersionQuery } from 'components/hooks/latestVersionQueries';

import DownloadModalWrapper from 'components/application/DownloadModalWrapper';

import ActionButton from 'components/buttons/ActionButton';
import { useCurrentActor } from 'components/contexts/CurrentActor';

import { formatData } from 'components/helpers/reactQuery';

export default function LatestVersionDownloadButton({ projectId }) {
  const { user: currentUser } = useCurrentActor();
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const {
    data: domainLatestVersion,
    isError: isDomainLatestVersionError,
    isSuccess: isDomainLatestVersionSuccess,
    refetch: refetchDomainLatestVersion,
  } = useLatestVersionQuery({
    params: { include: 'version_pdf' },
    projectId,
    queryOptions: {
      enabled: !!projectId && isModalOpen,
      refetchInterval: (data) =>
        !data || data.data.relationships.versionPdf.data ? false : 1000,
      retry: false,
      select: (data) => formatData({ data, resourceTypes: ['versionPdf'] }),
      staleTime: 0,
    },
  });

  const downloadLink =
    isDomainLatestVersionSuccess &&
    domainLatestVersion.data.relationships.versionPdf.data?.id &&
    domainLatestVersion.included.versionPdf[
      domainLatestVersion.data.relationships.versionPdf.data.id
    ].links.content;

  const downloadStatus = {
    downloadFailed: isDomainLatestVersionError,
    downloadInProgress: !(isDomainLatestVersionError || downloadLink),
    downloadLink,
    downloadSucceeded: isDomainLatestVersionSuccess && !!downloadLink,
  };

  const onClick = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    handleReset();
  };

  const handleReset = () => {
    queryClient.removeQueries({
      exact: true,
      queryKey: [
        'projects',
        projectId,
        'latestVersion',
        { include: 'version_pdf' },
      ],
    });
  };

  const downloadAction = () => {
    saveAsFile(downloadLink, '_blank');
  };

  return (
    <>
      <ActionButton
        buttonText={i18nTShared('download')}
        disabledClasses='tw-text-grey-300 tw-bg-grey-050 tw-border-grey-100'
        onClick={onClick}
        styleClasses='app-button__group-member -tw-ml-px tw-apply-outlined-button tw-apply-outlined-button--md tw-apply-outlined-button--grey tw-apply-outlined-button--on-light hover:tw-z-10 active:tw-z-10'
      />
      {isModalOpen && (
        <DownloadModalWrapper
          analyticProps={{ currentUser, projectId }}
          analyticTrackingId='Project downloaded'
          closeModal={closeModal}
          downloadAction={downloadAction}
          downloadStatus={downloadStatus}
          modalIsOpen={isModalOpen}
          resetAction={handleReset}
          retryAction={refetchDomainLatestVersion}
        />
      )}
    </>
  );
}

LatestVersionDownloadButton.propTypes = {
  projectId: PropTypes.string.isRequired,
};
